/**
 * UserAvatar - Professional avatar component with initials
 * 
 * Displays user initials with gradient backgrounds and proper styling.
 * Uses inline styles for guaranteed consistency across all frontends.
 */
import { useMemo } from "react";

interface UserAvatarProps {
  name: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

// Beautiful gradient color pairs [from, to]
const AVATAR_GRADIENTS: readonly (readonly [string, string])[] = [
  ["#3B82F6", "#1D4ED8"], // Blue
  ["#10B981", "#059669"], // Green
  ["#8B5CF6", "#6D28D9"], // Purple
  ["#F59E0B", "#D97706"], // Amber
  ["#EC4899", "#DB2777"], // Pink
  ["#06B6D4", "#0891B2"], // Cyan
  ["#6366F1", "#4F46E5"], // Indigo
  ["#EF4444", "#DC2626"], // Red
  ["#14B8A6", "#0D9488"], // Teal
  ["#F97316", "#EA580C"], // Orange
] as const;

function getInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getGradientFromName(name: string): readonly [string, string] {
  if (!name) return AVATAR_GRADIENTS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

const sizes = {
  sm: { size: 28, fontSize: 11 },
  md: { size: 36, fontSize: 13 },
  lg: { size: 44, fontSize: 15 },
};

export function UserAvatar({ name, size = "md", className = "" }: UserAvatarProps) {
  const initials = useMemo(() => getInitials(name), [name]);
  const [gradientFrom, gradientTo] = useMemo(() => getGradientFromName(name), [name]);
  const { size: pixelSize, fontSize } = sizes[size];

  const style: React.CSSProperties = {
    width: pixelSize,
    height: pixelSize,
    minWidth: pixelSize,
    minHeight: pixelSize,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: fontSize,
    fontWeight: 600,
    letterSpacing: "0.02em",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    flexShrink: 0,
  };

  return (
    <div style={style} title={name} className={className}>
      {initials}
    </div>
  );
}
